import { Check, SquareArrowOutUpRight, MoreHorizontal } from "lucide-react";
import Link from "next/link";
import React from "react";

import { CardActions } from "@/src/components/cards/card-actions";
import { ComparisonField } from "@/src/components/cards/comparison-field";
import { CopyableContent } from "@/src/components/copyable-content";
import { Badge } from "@/src/components/shadcn/ui/badge";
import { Button } from "@/src/components/shadcn/ui/button";
import { DropdownMenu, DropdownMenuTrigger } from "@/src/components/shadcn/ui/dropdown-menu";
import { getNestedValue } from "@/src/lib/utils/object-utils";
import { compareStrings, getDisplayValue, normalizeForComparison, FieldType } from "@/src/lib/utils/string-utils";

export interface Field {
  key: string;
  label: string;
  type?: FieldType;
  comparable?: boolean;
  required?: boolean;
  colSpan?: 1 | 2;
  hideIfEmpty?: boolean;
  sectionHeader?: string;
  getValue?: (obj: any) => string | null | undefined;
}

function resolveFieldValue(field: Field, obj: any): string | null | undefined {
  if (field.getValue) {
    return field.getValue(obj);
  }

  return getNestedValue(obj, field.key);
}

export interface FieldDisplayProps {
  field: Field;
  primaryValue: string | null;
  secondaryValue?: string | null;
  isCompareMode: boolean;
  compareAgainstTitle?: string;
}

export function FieldDisplay({
  field,
  primaryValue,
  secondaryValue,
  isCompareMode,
  compareAgainstTitle = "EMR",
}: FieldDisplayProps) {
  if (isCompareMode && secondaryValue !== null && field.comparable) {
    return (
      <ComparisonField
        label={field.label}
        value={primaryValue || "N/A"}
        secondaryValue={secondaryValue || "N/A"}
        type={field.type ?? "text"}
        compareAgainstTitle={compareAgainstTitle}
      />
    );
  }

  const displayValue = getDisplayValue(primaryValue, field.type ?? "text");

  return (
    <div className="overflow-hidden text-ellipsis whitespace-nowrap">
      <span className="font-medium">{field.label}:</span> <CopyableContent text={displayValue} />
    </div>
  );
}

export function FieldLayout({
  fields,
  patient,
  secondaryObject,
  compareMode = false,
  compareAgainstTitle = "EMR",
}: {
  fields: Field[];
  patient: any;
  secondaryObject?: any;
  compareMode?: boolean;
  compareAgainstTitle?: string;
}) {
  return (
    <div className="grid grid-cols-3 gap-x-4 gap-y-2 text-sm">
      {fields
        .filter((field) => !field.hideIfEmpty || resolveFieldValue(field, patient))
        .map((field) => (
          <React.Fragment key={field.key}>
            {field.sectionHeader && (
              <div className="col-span-3 mt-1 border-t border-border pt-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {field.sectionHeader}
                </span>
              </div>
            )}
            <div className={field.colSpan === 2 ? "col-span-2" : ""}>
              <FieldDisplay
                field={field}
                primaryValue={resolveFieldValue(field, patient) || "N/A"}
                secondaryValue={secondaryObject ? resolveFieldValue(field, secondaryObject) || "N/A" : null}
                isCompareMode={compareMode}
                compareAgainstTitle={compareAgainstTitle}
              />
            </div>
          </React.Fragment>
        ))}
    </div>
  );
}

export function hasConflicts(fields: Field[], obj: any, secondaryObj: any) {
  const comparableFields = fields.filter((field) => field.comparable);

  return comparableFields.some((field) => {
    const fieldType = field.type ?? "text";
    const primaryValue = normalizeForComparison(getNestedValue(obj, field.key) || "", fieldType);
    const secondaryValue = normalizeForComparison(getNestedValue(secondaryObj, field.key) || "", fieldType);

    return !compareStrings(primaryValue, secondaryValue, fieldType);
  });
}

export function renderBadge(compareMode: boolean, secondaryObject: any, hasAnyConflicts: boolean) {
  if (!compareMode) {
    return null;
  }

  if (!secondaryObject) {
    return <MissingDataBadge />;
  }

  if (hasAnyConflicts) {
    return <ConflictsBadge />;
  }

  return <SyncedBadge />;
}

export function SyncedBadge() {
  return (
    <Badge variant="outline" className="text-success border-success/30 bg-success/10">
      <Check className="h-3 w-3 mr-1" />
      Synced
    </Badge>
  );
}

export function ConflictsBadge() {
  return (
    <Badge variant="outline" className="text-warn border-warn/30 bg-warn/10">
      Conflicts
    </Badge>
  );
}

export function EhrDataBadge() {
  return (
    <Badge variant="outline" className="bg-ehr/10 text-ehr">
      EHR Data
    </Badge>
  );
}

export function MissingDataBadge() {
  return (
    <Badge variant="outline" className="text-destructive border-destructive/30 bg-destructive/10">
      Missing EHR Data
    </Badge>
  );
}

export function LinkToEntity({ href }: { href: string }) {
  return (
    <Link className="text-muted-foreground hover:text-foreground" href={href} target="_blank" rel="noopener noreferrer">
      <SquareArrowOutUpRight className="size-4" />
    </Link>
  );
}

export function EntityDropdownMenu({
  cardActionsProps,
  customProps = [],
}: {
  cardActionsProps: {
    id: string;
    label: string;
    href?: string;
    customProps?: Array<{ label: string; onClick: () => void }>;
  };
  customProps?: Array<{ label: string; onClick: () => void }>;
}) {
  const mergedCustomProps = [...(cardActionsProps.customProps ?? []), ...customProps];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="flex h-8 w-8 p-0 data-[state=open]:bg-muted">
          <MoreHorizontal className="h-4 w-4" />
          <span className="sr-only">Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <CardActions {...cardActionsProps} isContextMenu={false} customProps={mergedCustomProps} />
    </DropdownMenu>
  );
}
